import { flatten } from 'lodash-es';

/**
 * Helper to split up fetches using a list of ids into batches.
 * Returns the results concatenated
 * @param batchSize the number of items to fetch per batch
 * @param collection the input list, for example ids
 * @param promiseCreator map subset of input objects/ids to a request
 */
export async function fetchInBatchesOf<TInput, TOutput>(
  batchSize: number,
  collection: Array<TInput>,
  promiseCreator: (inputs: Array<TInput>) => Promise<Array<TOutput>>
): Promise<Array<TOutput>>;
export async function fetchInBatchesOf<TInput, TOutput>(
  batchSize: number,
  collection: Array<TInput>,
  promiseCreator: (inputs: Array<TInput>) => Promise<TOutput>
): Promise<Array<TOutput>>;
export async function fetchInBatchesOf<TInput, TOutput>(
  batchSize: number,
  collection: Array<TInput>,
  promiseCreator: (inputs: Array<TInput>) => Promise<Array<TOutput>> | Promise<TOutput>
): Promise<Array<TOutput>> {
  const totalLength = collection.length;
  const results: Array<Array<TOutput>> = [];
  for (let index = 0; index < totalLength; index += batchSize) {
    const batch = await promiseCreator(collection.slice(index, index + batchSize));
    if (Array.isArray(batch)) {
      results.push(batch);
    } else {
      results.push([batch]);
    }
  }
  return flatten<TOutput>(results);
}

interface FetchPaginatedOptions {
  perPage: number;
  /** If specified, stops after a certain number of pages. Otherwise, keeps fetching until there's no more */
  pages?: number | null;
}
/**
 * Helper to split up a request into several smaller ones with page numbers.
 * Returns the results concatenated
 * @param perPage Number of items to fetch per batch
 * @param promiseCreator map page number to a request
 */
export async function fetchPaginated<T>(
  options: FetchPaginatedOptions,
  promiseCreator: (page: number, perPage: number) => Promise<Array<T>>
): Promise<Array<T>> {
  let lastBatchSize = options.perPage;
  let page = 1;
  const results: Array<Array<T>> = [];
  while (lastBatchSize === options.perPage && (options.pages == null || page <= options.pages)) {
    const batch = await promiseCreator(page, options.perPage);
    lastBatchSize = batch.length;
    results.push(batch);
    page += 1;
  }
  return flatten<T>(results);
}
